import { useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from './base';
import { CampoControlado, prontoParaConsulta } from './formulario';
import { Modal } from './sobreposicao';
import { useToast } from './toast';
import { useValidacaoLocal } from './validacao-local';
import { ErroRequisicao, get, mensagemDeErro, post } from '../nucleo/api';
import { useSessao } from '../nucleo/sessao';

/**
 * Fase 2 (W8, D17): cadastro rápido de fornecedor dentro da compra, pela rota do Cadastros (`POST /cadastros/parceiros`,
 * permissão `cadastros.criar`), no lugar do "terceiro avulso" do antigo: nome e documento opcional. O tipo sai do
 * documento (11 dígitos é pessoa; sem documento ou com 14, empresa). Fase 2 (W24, K9): saiu do Compras para os componentes
 * (textos no núcleo, `i18n/fornecedor-rapido-i18n.ts`) e o Contas usa o mesmo na conta a pagar (`origem: 'contas'`).
 */
export function NovoFornecedor({ aberto, aoFechar, aoCriar, origem = 'compras' }: { aberto: boolean; aoFechar: () => void; aoCriar: (f: { id: string; nome: string }) => void; origem?: 'compras' | 'contas' }) {
  if (!aberto) return null;
  return <Corpo aoFechar={aoFechar} aoCriar={aoCriar} origem={origem} />;
}

/** Fase 2 (W24, K9): o Contas reusa este cadastro rápido na conta a pagar (`origem: 'contas'`). */
function Corpo({ aoFechar, aoCriar, origem }: { aoFechar: () => void; aoCriar: (f: { id: string; nome: string }) => void; origem: 'compras' | 'contas' }) {
  const { t } = useTranslation();
  const tn = t;
  const toast = useToast();
  const [id] = useState(() => crypto.randomUUID());
  const [nome, setNome] = useState('');
  const [documento, setDocumento] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});
  const { ref } = useValidacaoLocal(erros, erro);
  // crm5 (E3): CNPJ completo e válido consulta a Receita pelo servidor (a mesma consulta do cadastro, `cadastros.ver`) e
  // preenche o nome só se ainda vazio. Falha não impede o cadastro.
  const s = useSessao();
  const [consulta, setConsulta] = useState<'buscando' | 'preenchido' | 'naoEncontrado' | 'falha' | null>(null);
  const ultima = useRef('');
  async function consultarCnpj(cnpj: string) {
    setConsulta('buscando');
    try {
      const r = await get<{ habilitada: boolean; situacao: string; dados: Record<string, unknown> | null }>(`/cadastros/consultas/cnpj/${cnpj}`);
      if (ultima.current !== cnpj) return;
      if (!r.habilitada) return setConsulta(null);
      if (r.situacao !== 'encontrado' || !r.dados) return setConsulta(r.situacao === 'nao_encontrado' ? 'naoEncontrado' : 'falha');
      const nomeReceita = [r.dados.razao_social, r.dados.nome_fantasia].find((x): x is string => typeof x === 'string' && !!x.trim());
      if (nomeReceita) setNome((x) => (x.trim() ? x : nomeReceita.trim()));
      setConsulta('preenchido');
    } catch {
      if (ultima.current === cnpj) setConsulta('falha');
    }
  }
  function mudarDocumento(v: string) {
    setDocumento(v);
    const pronto = s.pode('cadastros.ver') ? prontoParaConsulta({ tipo: 'documento' }, v) : null;
    if (!pronto || pronto === ultima.current) return;
    ultima.current = pronto;
    void consultarCnpj(pronto);
  }

  const criar = useMutation({
    mutationFn: async () => {
      const digitos = documento.replace(/\D/g, '');
      await post('/cadastros/parceiros', { id, tipo: digitos.length === 11 ? 'pessoa' : 'empresa', nome: nome.trim(), documento: documento.trim() || null, e_fornecedor: true, origem });
      return { id, nome: nome.trim() };
    },
    onSuccess: (f) => {
      toast.sucesso(t('fornecedorRapido.criado'));
      aoCriar(f);
      aoFechar();
    },
    onError: (e) => {
      setErros(e instanceof ErroRequisicao ? e.porCampo() : {});
      setErro(mensagemDeErro(e));
    },
  });

  function enviar(ev: FormEvent) {
    ev.preventDefault();
    // O modal fica num portal: sem isto, o submit sobe pela árvore do React até o formulário da compra.
    ev.stopPropagation();
    setErro(null);
    setErros({});
    criar.mutate();
  }

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('fornecedorRapido.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-fornecedor-rapido" carregando={criar.isPending}>
            {t('fornecedorRapido.salvar')}
          </Botao>
        </>
      }
    >
      <form ref={ref} id="form-fornecedor-rapido" onSubmit={enviar} noValidate className="flex flex-col gap-4">
        <p className="text-sm text-tinta-3">{t('fornecedorRapido.explicacao')}</p>
        {erro && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
            {erro}
          </p>
        )}
        <CampoControlado def={{ nome: 'nome', rotulo: t('fornecedorRapido.nome'), tipo: 'texto', obrigatorio: true, sensivel: 'nome' }} valor={nome} aoMudar={(v) => setNome(String(v ?? ''))} erro={erros.nome} />
        <CampoControlado
          def={{ nome: 'documento', rotulo: t('fornecedorRapido.documento'), tipo: 'documento', documento: 'qualquer', ajuda: consulta ? t(`fornecedorRapido.consulta.${consulta}`) : undefined }}
          valor={documento}
          aoMudar={(v) => mudarDocumento(String(v ?? ''))}
          erro={erros.documento}
        />
      </form>
    </Modal>
  );
}
